import { canvasFontFace, getCanvasNoteFont } from './canvasFonts'
import { DEFAULT_CANVAS_NOTE_HEIGHT, DEFAULT_CANVAS_NOTE_WIDTH } from '@/src/renderer/lib/canvas/canvasNoteSize'

// Farben und Maße liegen in `lib/canvas/` — die Mobile-App liest sie mit (Etappe 4c).
export * from '@/src/renderer/lib/canvas/canvasColors'

export * from '@/src/renderer/lib/canvas/canvasNoteSize'

/**
 * Grenzen beim Ziehen an den Eckpunkten. Unter ~80 trägt ein Zettel kaum ein
 * Wort — die Schrift ist bei 8px ohnehin am Boden (siehe
 * `computeStickyFontSize`). Über ~640 wird aus einer Notiz eine Fläche.
 *
 * **Beide liegen auf dem Punktraster:** 72 = 3 × 24, 648 = 27 × 24. Sonst
 * landete eine Notiz, die man bis an eine Grenze zieht, genau dort neben dem
 * Raster — und Einzel- und Gruppen-Skalierung verhielten sich verschieden, weil
 * `getGroupResize` seinerseits rasterkonform klemmen muss.
 *
 * Gerundet wurde nach außen (80 → 72, 640 → 648), damit die Umstellung nichts
 * wegnimmt, was vorher erreichbar war.
 */
export const MIN_CANVAS_NOTE_SIZE = 72
export const MAX_CANVAS_NOTE_SIZE = 648


/**
 * Um wie viel breiter ein **Notiz** als Rechteck ist.
 *
 * Wie in Miro und FigJam hat er genau ZWEI Breiten — Quadrat oder Rechteck —
 * und nichts dazwischen. „Etwas weniger als doppelt": 1,7.
 */
export const STICKY_WIDE_FACTOR = 1.7

/**
 * Die beiden erlaubten Breiten eines **Notiz** bei gegebener Seitenlänge.
 *
 * **Gerechnet in RASTERSCHRITTEN, nicht in Pixeln.** 168 × 1,7 sind 285,6 und
 * damit keine Rasterzahl; 288 ist eine (12 × 24) und weicht um 0,8 % ab. Für
 * den Preis sitzt jede Kante auf einem Punkt, wie alles andere auf dem Canvas.
 * Der tatsächliche Faktor wackelt dadurch je nach Zettelgröße zwischen 1,6 und
 * 1,75.
 *
 * **Bei einem Gleichstand gewinnt das schmalere Rechteck** (`ceil(x - 0.5)`
 * statt `round`). Fünf Rasterschritte liegen mit 8,5 genau zwischen acht und
 * neun; aufgerundet wäre der Faktor 1,8 und damit fast das Doppelte — das
 * gerade nicht gemeint ist. Abgerundet sind es 1,6, und die Form bleibt
 * erkennbar ein Zwischending.
 *
 * Der Faktor gilt auf die AKTUELLE Seitenlänge, nicht auf eine feste
 * Pixelzahl: Ein auf 240 gezogener Notiz wird 408 breit. Sonst hinge die
 * zweite Breite an einer Zahl, die mit dem Zettel nichts mehr zu tun hat.
 *
 * **Die Grenzen (`MIN`/`MAX_CANVAS_NOTE_SIZE`) gelten für die SEITE, nicht für
 * die Breite.** Das Rechteck zum größten Quadrat wäre 1104 breit; würde die
 * Grenze auch dafür gelten, könnte der größte Notiz gar kein Rechteck mehr
 * werden. Die Grenze gibt es, damit eine Notiz eine Notiz bleibt, und das
 * entscheidet seine Höhe samt der daran hängenden Schrift
 * (`computeStickyFontSize` nimmt die Obergrenze aus der Höhe).
 *
 * @example
 * stickyWidths(168, 24)   // { square: 168, wide: 288 }
 * stickyWidths(240, 24)   // { square: 240, wide: 408 }
 */
export function stickyWidths(side: number, grid: number): { square: number; wide: number } {
  const steps = (side / (grid > 0 ? grid : 1)) * STICKY_WIDE_FACTOR
  // `ceil(x - 0.5)` rundet zur MITTE hin ab statt auf — die Begründung steht
  // oben.
  const wide = Math.ceil(steps - 0.5) * (grid > 0 ? grid : 1)
  // Ein Rechteck, das nicht breiter wäre als sein Quadrat, wäre keins: Bei
  // einer Seite unter einem halben Rasterschritt rundete die Rechnung sonst
  // auf dieselbe Zahl.
  return { square: side, wide: Math.max(wide, side + (grid > 0 ? grid : 1)) }
}

/**
 * Welche der beiden Breiten der Zeiger meint.
 *
 * Der Kantenzug am **Notizen** ist ein SCHALTER, kein Regler: Die Breite
 * springt auf einen der beiden Werte, sobald der Zeiger die Mitte zwischen
 * ihnen überschreitet — kein Zwischenzustand, auch nicht für einen Frame. So
 * macht es FigJam, und es zeigt ehrlich, was beim Loslassen herauskommt.
 *
 * @param rawWidth die Breite unter dem Zeiger
 * @param side     die Seitenlänge (die Höhe des Zettels) — sie ändert sich nicht
 */
export function nextStickyWidth(rawWidth: number, side: number, grid: number): number {
  const { square, wide } = stickyWidths(side, grid)
  return rawWidth >= (square + wide) / 2 ? wide : square
}


/** Innenabstand des Zettels, spiegelt `padding` in NoteNode.scss. */
const NOTE_PADDING = 16
/**
 * Obergrenze als ANTEIL der Zettelhöhe, nicht als fester Wert: ein kurzes
 * Wort soll den Zettel füllen, egal wie groß er gezogen wurde. Mit einer
 * festen Grenze blieb die Schrift ab einer 240er Notiz stehen, während der
 * Zettel weiterwuchs — der Text schwamm dann in der Fläche.
 *
 * 0,38 ist die Grenze, die vorher bei der Vorgabegröße galt (64 / 168).
 */
const MAX_FONT_RATIO = 0.38
/**
 * Untergrenze. Bewusst tief: Miro schrumpft sehr lange Texte weit unter das
 * bequem Lesbare, bevor es sie abschneidet — der Zettel bleibt so als Ganzes
 * erkennbar, statt einen Absatz in Normalgröße anzureißen. Darunter läuft der
 * Text über und wird am ENDE abgeschnitten (Scrollbalken versteckt, siehe
 * SCSS) — auch das ist Miros Verhalten.
 */
const MIN_FONT_SIZE = 8
/**
 * Bezugsgröße der Breitenmessung. Schriftmaße skalieren linear mit der
 * Schriftgröße, deshalb wird jedes Wort EINMAL bei 100px gemessen und danach
 * heruntergerechnet — statt 53-mal je Kandidatengröße.
 *
 * Nachgemessen: Bei 12, 24, 61 und 64px weicht die skalierte Breite um
 * höchstens 0,01px von der direkt gemessenen ab.
 */
const MEASURE_REFERENCE_SIZE = 100

/**
 * Mittlere Zeichenbreite als Vielfaches der Schriftgröße — die NOTLÖSUNG,
 * wenn kein Canvas zum Messen da ist (jsdom, SSR).
 *
 * Bewusst großzügig statt „mittel": Zu klein geschätzt bricht der Browser den
 * Text um, den die Rechnung für einzeilig hielt, und schneidet ihn unten ab —
 * genau der Fehler, der hier behoben wurde. Zu groß geschätzt wird die Schrift
 * nur kleiner als nötig, und das sieht niemand.
 *
 * 0,72 deckt auch versalienlastigen Text ab (gemessen: „HAMBURGEFONS" 0,685
 * em/Zeichen). Ein einzelnes „m" liegt mit 0,94 darüber — für den Notfallpfad
 * ist das hinnehmbar, im Browser wird ohnehin gemessen.
 */
const FALLBACK_CHAR_WIDTH = 0.72
/**
 * Zeilenhöhe, spiegelt `line-height` in NoteNode.scss — wer hier dreht,
 * dreht dort mit.
 *
 * **1,36 ist die Inhaltsfläche von Open Sans**, nicht eine gewählte Zahl:
 * Ascent 1,07 em + Descent 0,29 em (gemessen über
 * `fontBoundingBoxAscent`/`-Descent`). Es waren 1,25 — also 0,11 em zu eng.
 * Der Überschuss ragt oben und unten aus dem Zeilenkasten, und weil das
 * Textfeld sich per `field-sizing: content` allein nach der ZEILENHÖHE bemisst,
 * wurde die Unterlänge der letzten Zeile abgeschnitten. Sichtbar als „unten
 * leicht abgeschnitten", unabhängig von der Zettelgröße.
 *
 * Gegengemessen im Browser: Bei 1,25 lief in 126 von 153 Proben etwas über
 * (bis zu 13px), bei 1,36 in keiner einzigen.
 */
const DEFAULT_LINE_HEIGHT = 1.36
/*
  Der Schriftschnitt zum Messen kommt seit der **Schriftauswahl** aus
  `canvasFonts.ts` und hängt am Zettel, nicht mehr an einer Konstante hier:
  Jede der acht Schriften läuft anders breit, und würde weiter gegen eine
  einzige gemessen, liefe eine Notiz in Permanent Marker über, während einer
  in Caveat unnötig klein bliebe.

  Der Ersatzstapel steht mit im `stack` (`'Caveat', cursive`) — solange die
  Schrift noch lädt, misst der Kontext dieselbe Ersatzschrift, die der Browser
  in dem Moment auch zeichnet. Die Rechnung stimmt also in JEDEM Zustand, sie
  muss nur beim Wechsel noch einmal laufen (siehe `subscribeCanvasFontReady`).
*/

/**
 * Misst die Breite eines Wortes bei `MEASURE_REFERENCE_SIZE`.
 *
 * Der Aufrufer kann eine eigene Funktion mitgeben — dafür ist der Parameter da:
 * Die Tests reichen echte, aus der Schriftdatei gezogene Maße herein, statt die
 * Schätzung der Implementierung zu doppeln.
 */
export type MeasureWord = (word: string) => number

/**
 * Der Messapparat: ein Canvas-Kontext, einmal angelegt, plus ein Wortspeicher.
 *
 * **`measureText` und nicht ein Probe-Render.** Der frühere Kommentar hier
 * verwarf das Messen mit dem Argument, es koste „einen Layout-Durchgang pro
 * Tastendruck" und liefere im inaktiven Tab (`display: none`) ohnehin nichts.
 * Das gilt für ein Element im DOM — `measureText` rechnet aber allein aus den
 * Schriftmetriken, ohne Layout und ohne Sichtbarkeit. Die TabStack-Falle aus
 * CLAUDE.md greift hier also nicht.
 *
 * `null`, wo es kein Canvas gibt (jsdom, SSR) — dann übernimmt die Schätzung.
 */
let measureContext: CanvasRenderingContext2D | null | undefined
const measureCache = new Map<string, number>()
/**
 * Deckel für den Wortspeicher. Ein Canvas kann viele tausend Notizen tragen;
 * ohne Grenze wüchse die Karte mit jedem je getippten Wort.
 */
const MEASURE_CACHE_LIMIT = 2000

/*
  ── Der Schriftwechsel ────────────────────────────────────────────────────

  `@font-face` trägt `font-display: swap` und eine `unicode-range` (globals.scss).
  „Open Sans" wird also NACHGELADEN, und bis dahin zeichnet der Browser eine
  Ersatzschrift. Der Canvas misst dann dieselbe Ersatzschrift — die Rechnung
  ist in dem Moment richtig.

  Falsch wird sie beim WECHSEL: Der Browser tauscht die Schrift aus, die
  einmal gewählte Schriftgröße bleibt aber stehen. Gemessen (Ersatzschrift
  gegen Open Sans, Vorgabe-Zettel):

    beim ersten Rendern  „Here" → 216,75/100em → 62px gewählt
    nach dem Laden       „Here" → 230,18/100em → bei 62px sind das 142,7px
                                                  gegen 136px Innenbreite
                                                  ⇒ Umbruch mitten im Wort

  Der Wortspeicher zu leeren genügt dafür NICHT — das rechnet nichts neu. Die
  Zettel müssen erfahren, dass sich die Grundlage geändert hat; dafür ist die
  Fassung unten da (`NoteNode` hängt sich per `useSyncExternalStore`
  daran).
*/
let fontVersion = 0
const fontListeners = new Set<() => void>()

/** Zählt hoch, sobald sich die gemessene Schrift geändert hat. */
export function getCanvasFontVersion(): number {
  return fontVersion
}

/** Meldet einen Zuhörer für den Schriftwechsel an. Gibt das Abmelden zurück. */
export function subscribeCanvasFontReady(listener: () => void): () => void {
  fontListeners.add(listener)
  return () => { fontListeners.delete(listener) }
}

function announceFontChange(): void {
  measureCache.clear()
  lineHeightCache.clear()
  inkOverhangCache.clear()
  fontVersion++
  for (const listener of fontListeners) listener()
}

function getMeasureContext(): CanvasRenderingContext2D | null {
  if (measureContext !== undefined) return measureContext

  measureContext = null
  if (typeof document === 'undefined') return measureContext

  const context = document.createElement('canvas').getContext('2d')
  if (!context) return measureContext
  measureContext = context

  return measureContext
}

/**
 * Welche Schriften schon angefordert wurden.
 *
 * Aktiv anfordern statt abzuwarten: Ohne das lädt eine Schrift erst, wenn
 * irgendwo Text in ihrer `unicode-range` gezeichnet wird — und `fonts.ready`
 * wäre bis dahin längst erfüllt. Je Schlüssel genau einmal, sonst hinge an
 * jedem Tastendruck ein neues Versprechen.
 */
const requestedFonts = new Set<string>()

function requestFont(fontKey: string | null | undefined): void {
  const key = fontKey ?? 'default'
  if (requestedFonts.has(key)) return
  requestedFonts.add(key)
  const font = getCanvasNoteFont(fontKey)
  // `FontFaceSet.load` will genau EINEN Schnitt, keinen Ersatzstapel — also
  // die erste Familie aus dem Stack.
  const family = font.stack.split(',')[0]!.trim()
  document.fonts?.load(`${font.weight} ${MEASURE_REFERENCE_SIZE}px ${family}`)
    .then(announceFontChange, () => {})
}

/**
 * Die Messfunktion für eine Schrift: im Browser gemessen, sonst geschätzt.
 *
 * **Der Wortspeicher trägt die Schrift im Schlüssel** (`caveat|Konzept`). Ohne
 * das bekäme ein Zettel die Breiten des zuletzt gemessenen anderen — der
 * häufigste Weg, sich beim Nachrüsten mehrerer Schriften eine falsche
 * Zeilenzahl einzufangen.
 */
function makeMeasureWord(fontKey: string | null | undefined): MeasureWord {
  const cachePrefix = `${fontKey ?? 'default'}|`
  return word => {
    const cacheKey = cachePrefix + word
    const cached = measureCache.get(cacheKey)
    if (cached !== undefined) return cached

    const context = getMeasureContext()
    let width: number
    if (context) {
      context.font = canvasFontFace(fontKey, MEASURE_REFERENCE_SIZE)
      width = context.measureText(word).width
      requestFont(fontKey)
    } else {
      width = word.length * FALLBACK_CHAR_WIDTH * MEASURE_REFERENCE_SIZE
    }

    if (measureCache.size >= MEASURE_CACHE_LIMIT) measureCache.clear()
    measureCache.set(cacheKey, width)
    return width
  }
}

/**
 * Die Zeilenhöhe einer Schrift als Vielfaches der Schriftgröße — ihre
 * Inhaltsfläche, also Ascent + Descent.
 *
 * **Gemessen und nicht gesetzt**, seit es acht Schriften gibt: 1,36 war die
 * Inhaltsfläche von Open Sans. Permanent Marker und Caveat laufen höher; mit
 * der fremden Zahl würde die Unterlänge der letzten Zeile abgeschnitten —
 * derselbe Fehler, der hier für Open Sans schon einmal behoben wurde.
 *
 * Dieselbe Zahl geht als `--note-line-height` ins SCSS. Rechnung und
 * Darstellung dürfen hier nicht auseinanderlaufen.
 */
const lineHeightCache = new Map<string, number>()

export function getNoteLineHeight(fontKey: string | null | undefined): number {
  const key = fontKey ?? 'default'
  const cached = lineHeightCache.get(key)
  if (cached !== undefined) return cached

  const context = getMeasureContext()
  let value = DEFAULT_LINE_HEIGHT
  if (context) {
    context.font = canvasFontFace(fontKey, MEASURE_REFERENCE_SIZE)
    const metrics = context.measureText('Hxg')
    const ascent = metrics.fontBoundingBoxAscent
    const descent = metrics.fontBoundingBoxDescent
    // jsdom liefert für beide `undefined`; dann bleibt es bei der Vorgabe.
    if (typeof ascent === 'number' && typeof descent === 'number' && ascent + descent > 0) {
      value = (ascent + descent) / MEASURE_REFERENCE_SIZE
    }
    requestFont(fontKey)
  }

  lineHeightCache.set(key, value)
  return value
}

/**
 * Wie weit die Glyphen einer Schrift über ihren Vorschub hinausragen — als
 * Vielfaches der Schriftgröße, der größere der beiden Ränder.
 *
 * **Warum:** `measureText().width` ist der VORSCHUB, nicht die Tinte. Bei einer
 * schrägen Handschrift wie Caveat reicht das letzte „o" von „Hello" gut über
 * ihn hinaus; die Rechnung hielt das Wort für passend, und das Feld schnitt
 * es rechts ab (gemeldet 2026-09-24). Zentriert gesetzt kann der Überhang an
 * beiden Rändern landen — `countLines` hält ihn deshalb links UND rechts frei.
 *
 * Je Schrift einmal über alle Buchstaben gemessen, wie `getNoteLineHeight`.
 * Ein Wort-genaues Maß brächte kaum mehr: Auch dort zählt allein der Rand.
 */
const inkOverhangCache = new Map<string, number>()
const OVERHANG_PROBE = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZäöüÄÖÜß0123456789'

export function getNoteInkOverhang(fontKey: string | null | undefined): number {
  const key = fontKey ?? 'default'
  const cached = inkOverhangCache.get(key)
  if (cached !== undefined) return cached

  const context = getMeasureContext()
  let value = 0
  if (context) {
    context.font = canvasFontFace(fontKey, MEASURE_REFERENCE_SIZE)
    for (const char of OVERHANG_PROBE) {
      const metrics = context.measureText(char)
      // jsdom und alte Engines liefern keine Tintenmaße — dann bleibt es bei 0.
      if (typeof metrics.actualBoundingBoxRight !== 'number') break
      const right = metrics.actualBoundingBoxRight - metrics.width
      const left = metrics.actualBoundingBoxLeft
      value = Math.max(value, right, left)
    }
    value /= MEASURE_REFERENCE_SIZE
    requestFont(fontKey)
  }

  inkOverhangCache.set(key, value)
  return value
}

/**
 * Zeilen, die `words` bei `fontSize` belegen — oder `null`, wenn es bei dieser
 * Größe gar nicht geht, weil ein einzelnes Wort breiter wäre als der Zettel.
 * Ein umgebrochenes Wort sieht falsch aus; lieber kleiner setzen (so hält es
 * auch Miro).
 */
function countLines(
  words: string[],
  fontSize: number,
  fieldWidth: number,
  measure: MeasureWord,
  overhang: number,
): number | null {
  const scale = fontSize / MEASURE_REFERENCE_SIZE
  const spaceWidth = measure(' ') * scale
  // Der Überhang (`getNoteInkOverhang`) an beiden Rändern.
  const innerWidth = fieldWidth - 2 * overhang * fontSize

  let lines = 1
  let filled = 0
  for (const word of words) {
    const wordWidth = measure(word) * scale
    if (wordWidth > innerWidth) return null
    const needed = filled === 0 ? wordWidth : filled + spaceWidth + wordWidth
    if (needed > innerWidth) {
      lines++
      filled = wordWidth
    } else {
      filled = needed
    }
  }
  return lines
}

/**
 * Adaptive font size for a Notiz (Miro-style: shorter text renders larger).
 * No manual override in the MVP — see the "Notiz-Resize" open question in
 * project-canvas.md.
 *
 * Sucht die größte Schrift zwischen der Untergrenze und dem Höhenanteil, bei
 * der der Text noch ganz auf den Zettel passt: Der Umbruch wird dafür wirklich
 * durchgespielt (`countLines`), statt die Zeilenzahl aus der Zeichenzahl zu
 * schätzen — eine Schätzung lag regelmäßig eine Zeile zu niedrig, weil
 * Wortumbrüche Zeilenenden frei lassen, und der Text wurde oben abgeschnitten.
 *
 * **Die Breiten werden GEMESSEN, nicht geschätzt.** Hier stand eine mittlere
 * Zeichenbreite von 0,55 em, und die trifft für kaum ein Wort zu: „Here" misst
 * 0,58 em/Zeichen, „Konzept" 0,56, ein „m" 0,94. Für „Here" auf dem
 * Vorgabe-Zettel bedeutete das 134px geschätzt gegen 140px echt — die Funktion
 * gab 61px zurück, der Browser brach mitten im Wort um („Her" / „e"), und die
 * zweite Zeile lief unten aus dem Zettel.
 *
 * Passt selbst die Untergrenze nicht mehr, gewinnt die Lesbarkeit: der Text
 * läuft über und das Feld wird scrollbar (Scrollbalken ausgeblendet, siehe
 * SCSS).
 */
export interface StickyFontOptions {
  /**
   * Die Schrift des Zettels. Sie entscheidet über die gemessenen Breiten UND
   * über die Zeilenhöhe — beides läuft je Schrift auseinander.
   */
  fontKey?: string | null
  /**
   * Eine eigene Messfunktion. Dafür ist der Parameter da: Die Tests reichen
   * echte, aus der Schriftdatei gezogene Maße herein, statt die Schätzung der
   * Implementierung zu doppeln.
   */
  measure?: MeasureWord
  /** Ein eigener Überhang (siehe `getNoteInkOverhang`) — aus demselben Grund wie `measure`. */
  overhang?: number
}

export function computeStickyFontSize(
  content: string,
  width: number = DEFAULT_CANVAS_NOTE_WIDTH,
  height: number = DEFAULT_CANVAS_NOTE_HEIGHT,
  options: StickyFontOptions = {},
): number {
  const { fontKey = null, measure = makeMeasureWord(fontKey), overhang = getNoteInkOverhang(fontKey) } = options
  const lineHeight = getNoteLineHeight(fontKey)
  /*
    Der **optische Ausgleich** wirkt allein auf die GRENZEN, nicht auf das
    Ergebnis: Caveat darf höher hinauf und tiefer hinunter, weil es bei
    gleicher Zahl kleiner wirkt. Die Suche dazwischen misst weiter in der
    Größe, in der auch gezeichnet wird — ein nachträglich multipliziertes
    Ergebnis liefe über, weil es in einer anderen Größe gemessen wurde als
    gesetzt.
  */
  const { scale } = getCanvasNoteFont(fontKey)
  const text = content.trim()
  const maxFontSize = Math.round(height * MAX_FONT_RATIO * scale)
  // Der leere Zettel startet groß: der Caret soll gleich sichtbar sein,
  // und das erste Wort schrumpft ohnehin sofort auf sein Maß.
  if (text.length === 0) return maxFontSize

  const innerWidth = Math.max(1, width - 2 * NOTE_PADDING)
  const innerHeight = Math.max(1, height - 2 * NOTE_PADDING)
  const words = text.split(/\s+/)

  // Absteigend statt Binärsuche: „passt" ist über die Größen nicht lückenlos
  // monoton (der Umbruch springt), und ein paar Dutzend Schritte über eine
  // Handvoll Wörter kosten dank des Wortspeichers nichts.
  const minFontSize = Math.round(MIN_FONT_SIZE * scale)
  for (let size = maxFontSize; size > minFontSize; size--) {
    const lines = countLines(words, size, innerWidth, measure, overhang)
    if (lines !== null && lines * lineHeight * size <= innerHeight) return size
  }
  return minFontSize
}
